<template>
  <div class="d-flex flex-column substep">
    <span class="primary-icon mr-1">
      <v-icon
        width="24"
        height="24"
        color="#175E51"
        class="mr-1 results-text-icon"
      >
        {{ substep.icon }}
      </v-icon>
    </span>
    <v-card-title class="pa-0 d-flex align-center">
      <span class="title substep-title no-truncate">
        {{ substep.title }}
      </span>
    </v-card-title>
    <v-card-text class="pa-0">
      <span class="text substep-text">
        {{ substep.content }}
      </span>
    </v-card-text>
    <v-btn
      v-if="substep.button"
      color="#175E51"
      elevation="0"
      class="button mt-2"
      @click="substep.button.action"
    >
      <span class="button-text button-text-primary">{{ substep.button.label }}</span>
    </v-btn>
  </div>
</template>

<script lang="ts" setup>
import { PropType } from 'vue'

import { Substep } from '@/modules/landingpage/models/Substep'

defineProps({
  substep: {
    type: Object as PropType<Substep>,
    required: true,
  },
})
</script>

<style scoped>
.substep {
  height: 100%;
}

.title {
  color: white;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px;
}

.text {
  color: white;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
}

.button-text,
.button-text-primary {
  color: #175e51;
  text-align: center;
  font-feature-settings:
    'liga' off,
    'clig' off;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.button-text-primary {
  color: white;
}

.vertical-divider {
  height: 28px;
}

.results-text {
  color: white;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  padding-top: 5px;
}
.results-text-number {
  color: white;
  font-size: 26px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
}

.results-text-icon {
  font-size: 22px;
  padding-left: 4px;
}
.step-3-title,
.step-3-text,
.substep-title,
.substep-text {
  color: #414142;
}

.substep-title {
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px;
}

.substep-text {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
}

.substep-card {
  padding: 10px 15px 15px 15px;
}

.substep-card-bg {
  background: #eef4f4;
}

.borders-right {
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
}

.borders-left {
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
}

.primary-icon,
.view-results-icon {
  background-color: #d8f4f4;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.view-results-icon {
  background-color: rgba(255, 255, 255, 0.2);
}

.link-text {
  color: white;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}

.no-truncate {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}

.v-card .v-card-title {
  line-height: 0;
}

.arrows-wrapper {
  height: 30px;
}

/* Shared styles for horizontal arrows */
.horizontal-arrow {
  display: flex;
  position: relative;
  height: 2px;
  background-color: #9e9e9e;
  /* transform: translate(-50%, -50%); */
  z-index: 99;
}

/* Shared styles for vertical arrows */
.vertical-arrow {
  display: flex;
  position: relative;
  height: 40px;
  width: 2px;
  background-color: #9e9e9e;
  z-index: 99;
}

.vertical-arrow::after {
  content: '';
  position: absolute;
  bottom: -8px; /* Arrowhead position below the line */
  width: 8px; /* Arrowhead width */
  height: 8px; /* Arrowhead height */
  background-color: #9e9e9e; /* Arrowhead color */
  clip-path: polygon(0% 0%, 100% 0%, 50% 100%);
}

/* Specific vertical arrow positions */
.vertical-arrow-first,
.vertical-arrow-third {
  left: 13%;
  top: -55%;
}

.vertical-arrow-third {
  top: 59.5%;
}

.vertical-arrow-second,
.vertical-arrow-forth,
.vertical-arrow-fifth,
.vertical-arrow-sixth {
  height: 10px;
  left: 46.5%;
  top: -94.5%;
}

.vertical-arrow-forth {
  left: 35.5%;
  top: -94.5%;
}

.vertical-arrow-fifth {
  left: 59%;
  top: -129%;
}

.vertical-arrow-sixth {
  left: 83.8%;
  top: -162.5%;
}

/* Specific adjustments for arrowheads */
.vertical-arrow-first::after,
.vertical-arrow-third::after,
.vertical-arrow-second::after,
.vertical-arrow-forth::after,
.vertical-arrow-fifth::after,
.vertical-arrow-sixth::after {
  left: -150%;
}

/* Specific horizontal arrow positions */
.horizontal-arrow-first {
  width: 250px;
  left: 13%;
  top: -95%;
}

.horizontal-arrow-second {
  width: 525px;
}

.step-card-mobile {
  display: none;
}

@media (max-width: 960px) {
  .button {
    width: 100%;
  }

  .borders-left {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .borders-right {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .border-bottom {
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
  }

  .border-top {
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }
}
</style>
